import { apiClient } from '@/api/api'
import type { TokensResponse } from '@/features/auth/api'
import { hasProfileApi } from '@/features/profile/flags'

/**
 * Account actions of Personal data (BE-147: `/auth/change-email`, `/auth/change-password`,
 * `/auth/resend-verification`). Each calls the API when its capability is on (dev default, release
 * builds via `VITE_PROFILE_API`); off, a dev mock walks the same Figma states.
 */

const MOCK_LATENCY_MS = 600

/** The mock's way to reach the error states: `taken@…` / `wrong` as the current password. */
export const PROFILE_MOCK_TAKEN_EMAIL_PREFIX = 'taken@'
export const PROFILE_MOCK_WRONG_PASSWORD = 'wrong'

/** The refusals of `/auth/change-email|change-password|resend-verification` (BE-147), plus `unknown`. */
export const PROFILE_ACCOUNT_ERROR_CODES = [
  'email_taken',
  'email_unchanged',
  'email_managed_by_provider',
  'wrong_password',
  'password_unchanged',
  'password_policy',
  'password_not_applicable',
  'resend_cooldown',
  'already_verified',
  'unknown',
] as const

export type ProfileAccountErrorCode = (typeof PROFILE_ACCOUNT_ERROR_CODES)[number]

export class ProfileAccountError extends Error {
  constructor(
    public readonly code: ProfileAccountErrorCode,
    /** The server's own sentence (`password_policy: <Cognito rule>`), when it sent one. */
    public readonly serverMessage: string | null = null,
  ) {
    super(code)
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * The wire refusal → one code. The backend answers `{detail: "<code>"}` (or `"password_policy: …"`);
 * a 401 on change-password is the wrong current password, a 429 on resend is the cooldown.
 */
export function toProfileAccountError(error: unknown): ProfileAccountError {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  const status = response?.status
  const detail = typeof response?.data?.detail === 'string' ? response.data.detail : ''
  const head = detail.split(':')[0]?.trim() ?? ''
  const known = (PROFILE_ACCOUNT_ERROR_CODES as readonly string[]).includes(head)
  if (known) return new ProfileAccountError(head as ProfileAccountErrorCode, detail.includes(':') ? detail : null)
  if (status === 401) return new ProfileAccountError('wrong_password')
  if (status === 429) return new ProfileAccountError('resend_cooldown')
  return new ProfileAccountError('unknown')
}

export const requestEmailChange = async (newEmail: string): Promise<void> => {
  if (!hasProfileApi('change_email')) {
    await wait(MOCK_LATENCY_MS)
    if (newEmail.trim().toLowerCase().startsWith(PROFILE_MOCK_TAKEN_EMAIL_PREFIX)) {
      throw new ProfileAccountError('email_taken')
    }
    return
  }
  try {
    // 202 `{status: "verification_sent", email}` — a signed 24 h link goes to the NEW address.
    await apiClient.post('/auth/change-email', { email: newEmail.trim() })
  } catch (error) {
    throw toProfileAccountError(error)
  }
}

/**
 * Decision 15: a password change signs out every device — this one included: the token that
 * called the route is refused from the next request on. The answer carries this device's NEW
 * tokens (`auth/` is not camelised); the caller stores them. `null` from the dev mock and from
 * an older API that answered without tokens.
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<TokensResponse | null> => {
  if (!hasProfileApi('change_password')) {
    await wait(MOCK_LATENCY_MS)
    if (currentPassword === PROFILE_MOCK_WRONG_PASSWORD) {
      throw new ProfileAccountError('wrong_password')
    }
    return null
  }
  try {
    const response = await apiClient.post<Partial<TokensResponse> & { status?: string }>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
    return changedPasswordTokens(response.data)
  } catch (error) {
    throw toProfileAccountError(error)
  }
}

/** The new session of `POST /auth/change-password` 200, `null` when the body has none. */
export function changedPasswordTokens(body: unknown): TokensResponse | null {
  const data = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
  if (typeof data.access_token !== 'string' || !data.access_token || typeof data.expires_in !== 'number') return null
  return {
    access_token: data.access_token,
    expires_in: data.expires_in,
    ...(typeof data.refresh_token === 'string' ? { refresh_token: data.refresh_token } : {}),
    ...(typeof data.refresh_expires_in === 'number' ? { refresh_expires_in: data.refresh_expires_in } : {}),
  }
}

export const resendEmailVerification = async (): Promise<void> => {
  if (!hasProfileApi('email_verification')) {
    await wait(MOCK_LATENCY_MS)
    return
  }
  try {
    await apiClient.post('/auth/resend-verification')
  } catch (error) {
    throw toProfileAccountError(error)
  }
}
